import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@/integrations/supabase/types';

type AppRole = Database['public']['Enums']['app_role'];

export function useUserRoles() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['user-roles', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);

      if (error) throw error;
      return data.map(r => r.role);
    },
    enabled: !!user,
  });
}

export function useHasRole(role: AppRole) {
  const { data: roles, isLoading } = useUserRoles();
  return {
    hasRole: roles?.includes(role) ?? false,
    isLoading,
  };
}

export function useIsAdmin() { return useHasRole('admin'); }
export function useIsInstructor() { return useHasRole('instructor'); }
export function useIsSuperAdmin() { return useHasRole('super_admin'); }
export function useIsSchoolAdmin() { return useHasRole('school_admin'); }
export function useIsCFO() { return useHasRole('cfo'); }
export function useIsCOO() { return useHasRole('coo'); }

// CEO = super_admin (top of executive chain)
export function useIsCEO() { return useHasRole('super_admin'); }
